import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, PlusIcon, MinusIcon, CheckIcon, ArrowLeftIcon } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import Button from '../../components/ui/Button';
// Mock food data
const foodCategories = [{
  id: 'all',
  name: 'All Items'
}, {
  id: 'special',
  name: 'Special Menu'
}, {
  id: 'healthy',
  name: 'Healthy Food'
}, {
  id: 'breakfast',
  name: 'Breakfast'
}, {
  id: 'lunch',
  name: 'Lunch'
}, {
  id: 'dinner',
  name: 'Dinner'
}, {
  id: 'vegetarian',
  name: 'Vegetarian'
}, {
  id: 'dessert',
  name: 'Desserts'
}];
const foodItems = Array(15).fill(null).map((_, index) => ({
  id: `food-${index + 1}`,
  name: 'Fried Egg with Tomato',
  chineseName: '番茄炒鸡蛋',
  price: 7.5,
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rd7ZuDhGdqSuEjdsymkVFA/image.png',
  category: index % 7,
  isSpecial: index < 2
}));
const FoodBrowsing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    halal: false,
    vegetarian: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [addedItems, setAddedItems] = useState<Record<string, boolean>>({});
  const {
    addItem,
    items
  } = useCart();
  // Check if we have a category from the location state
  useEffect(() => {
    const stateCategory = location.state?.category;
    if (stateCategory) {
      const matchedCategory = foodCategories.find(cat => cat.id === stateCategory || cat.name.toLowerCase() === stateCategory);
      if (matchedCategory) {
        setActiveCategory(matchedCategory.id);
      }
    }
  }, [location.state]);
  // Sync cart items with addedItems state
  useEffect(() => {
    const itemsInCart: Record<string, boolean> = {};
    items.forEach(item => {
      itemsInCart[item.id] = true;
    });
    setAddedItems(itemsInCart);
  }, [items]);
  const handleFilterChange = (name: keyof typeof filters) => {
    setFilters(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };
  const filteredItems = searchQuery ? foodItems.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.chineseName?.includes(searchQuery)) : foodItems.filter(item => {
    // Category filter
    const categoryMatch = activeCategory === 'all' || foodCategories[item.category]?.id === activeCategory;
    // Additional filters
    const halalMatch = !filters.halal || item.id.includes('2'); // Mock halal filter
    const vegetarianMatch = !filters.vegetarian || item.id.includes('3'); // Mock vegetarian filter
    return categoryMatch && halalMatch && vegetarianMatch;
  });
  const handleAddToCart = (item: (typeof foodItems)[0]) => {
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      image: item.image,
      chineseName: item.chineseName
    });
    // Show feedback that item was added
    setAddedItems(prev => ({
      ...prev,
      [item.id]: true
    }));
  };
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  const handleGoBack = () => {
    navigate(-1);
  };
  return <div className="pb-20 md:pb-8" data-id="element-410">
      {/* Back button */}
      <div className="mb-4" data-id="element-411">
        <button onClick={handleGoBack} className="flex items-center text-gray-600 hover:text-[#E84118] transition-colors" aria-label="Go back" data-id="element-412">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-413" />
          <span data-id="element-414">Back</span>
        </button>
      </div>
      {/* Search and filters */}
      <div className="sticky top-16 md:top-0 bg-white z-10 shadow-sm pb-4" data-id="element-415">
        <div className="flex flex-col sm:flex-row gap-3 mb-3" data-id="element-416">
          <div className="relative flex-1" data-id="element-417">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-418">
              <SearchIcon size={18} className="text-gray-400" data-id="element-419" />
            </div>
            <input type="text" placeholder="What do you want to eat?" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#E84118] focus:border-[#E84118]" data-id="element-420" />
          </div>
          <button onClick={toggleFilters} className="sm:hidden flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white" data-id="element-421">
            <FilterIcon size={18} className="mr-2 text-gray-500" data-id="element-422" />
            Filters
          </button>
        </div>
        {/* Mobile filters */}
        {showFilters && <div className="sm:hidden mb-3 p-3 border border-gray-200 rounded-md bg-gray-50" data-id="element-423">
            <div className="flex flex-col space-y-2" data-id="element-424">
              <label className="flex items-center" data-id="element-425">
                <input type="checkbox" checked={filters.halal} onChange={() => handleFilterChange('halal')} className="h-4 w-4 text-[#E84118] focus:ring-[#E84118] rounded" data-id="element-426" />
                <span className="ml-2 text-sm" data-id="element-427">Halal Food</span>
              </label>
              <label className="flex items-center" data-id="element-428">
                <input type="checkbox" checked={filters.vegetarian} onChange={() => handleFilterChange('vegetarian')} className="h-4 w-4 text-[#E84118] focus:ring-[#E84118] rounded" data-id="element-429" />
                <span className="ml-2 text-sm" data-id="element-430">Vegetarian</span>
              </label>
            </div>
          </div>}
        {/* Desktop filters */}
        <div className="hidden sm:flex items-center space-x-3 mb-3" data-id="element-431">
          <label className="flex items-center" data-id="element-432">
            <input type="checkbox" checked={filters.halal} onChange={() => handleFilterChange('halal')} className="h-4 w-4 text-[#E84118] focus:ring-[#E84118] rounded" data-id="element-433" />
            <span className="ml-2 text-sm" data-id="element-434">Halal Food</span>
          </label>
          <label className="flex items-center" data-id="element-435">
            <input type="checkbox" checked={filters.vegetarian} onChange={() => handleFilterChange('vegetarian')} className="h-4 w-4 text-[#E84118] focus:ring-[#E84118] rounded" data-id="element-436" />
            <span className="ml-2 text-sm" data-id="element-437">Vegetarian</span>
          </label>
        </div>
        {/* Categories */}
        <div className="overflow-x-auto -mx-4 px-4 pb-1" data-id="element-438">
          <div className="flex space-x-2 min-w-max" data-id="element-439">
            {foodCategories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${activeCategory === category.id ? 'bg-[#E84118] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-440">
                {category.name}
              </button>)}
          </div>
        </div>
      </div>
      {/* Food items grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-4" data-id="element-441">
        {filteredItems.length > 0 ? filteredItems.map(item => <div key={item.id} className={`bg-white rounded-lg shadow-md overflow-hidden ${item.isSpecial ? 'border-2 border-[#E84118]' : ''} transform transition-all duration-300 hover:shadow-lg`} data-id="element-442">
              <div className="cursor-pointer" onClick={() => navigate(`/food/${item.id}`)} data-id="element-443">
                <img src={item.image} alt={item.name} className="w-full h-32 sm:h-40 object-cover" data-id="element-444" />
              </div>
              <div className="p-3" data-id="element-445">
                <div className="flex justify-between items-start" data-id="element-446">
                  <div className="cursor-pointer" onClick={() => navigate(`/food/${item.id}`)} data-id="element-447">
                    <h3 className="font-medium text-sm sm:text-base" data-id="element-448">
                      {item.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-449">
                      {item.chineseName}
                    </p>
                  </div>
                  {item.isSpecial && <span className="bg-[#E84118] text-white text-xs px-1.5 py-0.5 rounded" data-id="element-450">
                      Special
                    </span>}
                </div>
                <div className="mt-2 flex justify-between items-center" data-id="element-451">
                  <span className="font-bold text-sm sm:text-base" data-id="element-452">
                    ${item.price.toFixed(2)}
                  </span>
                  {addedItems[item.id] ? <div className="flex items-center" data-id="element-453">
                      <CheckIcon size={14} className="text-green-500 mr-1" data-id="element-454" />
                      <Button size="sm" variant="outline" onClick={() => navigate('/cart')} className="text-xs py-1 px-2" data-id="element-455">
                        View
                      </Button>
                    </div> : <button onClick={() => handleAddToCart(item)} className="w-7 h-7 rounded-full bg-[#E84118] text-white flex items-center justify-center hover:bg-[#d13a16] transition-colors" aria-label={`Add ${item.name} to cart`} data-id="element-456">
                      <PlusIcon size={14} data-id="element-457" />
                    </button>}
                </div>
              </div>
            </div>) : <div className="col-span-full text-center py-10" data-id="element-458">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-459">
              <SearchIcon size={24} className="text-gray-400" data-id="element-460" />
            </div>
            <h3 className="font-medium text-lg mb-2" data-id="element-461">No items found</h3>
            <p className="text-gray-500 mb-6" data-id="element-462">
              Try adjusting your search or filters
            </p>
            <Button onClick={() => {
          setSearchQuery('');
          setActiveCategory('all');
          setFilters({
            halal: false,
            vegetarian: false
          });
        }} data-id="element-463">
              Clear all filters
            </Button>
          </div>}
      </div>
      {/* Cart button for mobile */}
      {items.length > 0 && <div className="fixed bottom-20 inset-x-0 md:hidden flex justify-center z-10" data-id="element-464">
          <Button onClick={() => navigate('/cart')} className="shadow-lg px-6 py-2" data-id="element-465">
            View Cart ({items.length} items)
          </Button>
        </div>}
    </div>;
};
export default FoodBrowsing;